import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sultanActions, orgActions, uiActions } from '@/store';
import { setAuthToken } from '@/lib/trackizy';
import { useInstanceAccess } from '@/hooks/useInstanceAccess';
import { X } from 'lucide-react';

export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSupport, isStaff, can } = useInstanceAccess();

  const impersonatingOrgId = useSelector((state) => state.sultan.impersonatingOrgId);
  const impersonatingOrgName = useSelector((state) => state.sultan.impersonatingOrgName);
  const originalOrg = useSelector((state) => state.sultan.originalOrg);

  if (!impersonatingOrgId) return null;

  function handleExit() {
    // Restore the sultan's original JWT so API calls go back to their home org.
    if (originalOrg?.token) {
      setAuthToken(originalOrg.token);
    }
    // Restore original org state
    dispatch(orgActions.setOrg({
      org: originalOrg,
      role: originalOrg?.role ?? null,
      token: originalOrg?.token ?? null,
    }));
    // Clear impersonation state
    dispatch(sultanActions.exitImpersonation());
    // Flip back to platform sidebar so a sultan or support user lands in
    // Platform Admin mode. Reseller admins have no platform sidebar mode —
    // they exit straight to /reseller with the normal (non-platform) nav.
    if (isStaff) {
      dispatch(uiActions.setSidebarMode('platform'));
    }
    // Sultans/support return to the platform panel; reseller admins return
    // to their reseller overview.
    navigate(isStaff ? '/sultan' : '/reseller');
  }

  return (
    <div
      className="fixed top-0 inset-x-0 z-[200] h-9 flex items-center justify-center bg-amber-500 text-white text-xs font-semibold shadow-md"
      style={{ gap: '0.5rem' }}
    >
      <span>{t('sultan.viewingAs', { orgName: impersonatingOrgName })}</span>
      {isSupport && !can('delete') && (
        <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">{t('instance.noDeleteChip', 'Support · no delete')}</span>
      )}
      <span className="opacity-60 mx-1">—</span>
      <button
        type="button"
        onClick={handleExit}
        className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/20 hover:bg-white/30 transition-colors text-white text-xs font-semibold"
      >
        <X size={12} />
        {t('sultan.exitImpersonation')}
      </button>
    </div>
  );
}
